'use client';

import { useState, useEffect } from 'react';
import Image from "next/image";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';

// Carousel images array
const images = [
  {
    src: "/images/about/miller40.jpg",
    alt: "Miller 40 team of professional mechanics"
  },
  {
    src: "/images/about/team-about.jpg", 
    alt: "Miller 40 workshop facilities",
    objectPosition: "center 40%"
  },
  {
    src: "/images/about/team-about2.png",
    alt: "Miller 40 experienced technicians",
    objectPosition: "center 40%" 
  },
  {
    src: "/images/about/team-about4.jpg",
    alt: "Miller 40 technician performing diagnostic service"
  },
  {
    src: "/images/about/team-about5.jpg",
    alt: "Miller 40 mechanic working with professional automotive equipment"
  }
];

export function AboutHeroSection({ className }: { className?: string }) {
  const t = useTranslations('aboutHero');

  const description = t.raw('paragraphs') as string[];
  const stats = t.raw('stats') as Array<{ value: string; label: string }>;
  
  // Carousel state and logic
  const [currentImage, setCurrentImage] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Navigate to next image
  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  // Navigate to previous image
  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  // Auto-play carousel (pauses when hovered)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isHovered) {
        setCurrentImage((prev) => (prev + 1) % images.length);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isHovered]); //

  return (
    <section className={cn("py-16 relative bg-site", className)}>
      {/* Decorative background dots */}
      <img
        src={"/images/about/dots.png"}
        alt="Decorative dot pattern background element"
        className="object-contain top-[40%] absolute h-[25vh]"
      />
      <img
        src={"/images/about/dots.png"}
        alt="Decorative dot pattern background element"
        className="object-contain right-0 top-8 absolute h-[25vh]"
      />
      
      <div className="mx-4 md:mx-16 lg:mx-20">
        
        {/* Hero Carousel */}
        <div 
          className="relative rounded-3xl overflow-hidden mb-12 h-[400px] md:h-[600px] group"
          onMouseEnter={() => setIsHovered(true)} // Pause on hover
          onMouseLeave={() => setIsHovered(false)} // Resume on leave
        >
          {/* Current image */}
          <Image
            src={images[currentImage].src}
            alt={images[currentImage].alt}
            fill
            className="object-cover transition-opacity duration-700 ease-in-out"
            style={{
            objectPosition: images[currentImage].objectPosition || "center 80%"
          }}  
          />

          {/* Previous navigation button */}
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Next navigation button */}
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Dot indicators */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImage(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentImage 
                    ? 'bg-yellow-500 scale-110' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>

          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
        </div>

        {/* Description Text */}
        <div className="max-w-4xl mx-auto mb-16">
          {description.map((paragraph, index) => (
            <p
              key={index}
              className="text-white text-lg leading-relaxed mb-6 text-center"
            >
              {paragraph}
            </p>
          ))}
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center bg-site-card p-4 md:p-6 lg:p-8 rounded-[32px]"
            >
              <div className="text-2xl md:text-3xl font-bold text-yellow-500 md:mb-2">
                {stat.value}
              </div>
              <div className="text-white text-sm md:text-base">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}